// Canvas helpers shared by the renderer, the text and sprite caches and the asset placeholders
// (architecture §9.6: placeholders draw to an OffscreenCanvas, or a regular canvas as a fallback).
//
// One factory makes every offscreen bitmap: a DOM <canvas> on the main thread (which always sees
// the page's web fonts), an OffscreenCanvas in a worker, or a fake installed by tests with
// `setCanvasFactory` (Node has neither).

export type Canvas2D = HTMLCanvasElement | OffscreenCanvas;
export type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
export type CanvasFactory = (width: number, height: number) => Canvas2D;

let override: CanvasFactory | null = null;

/** Replaces the canvas factory (tests, workers); `null` restores the default. */
export function setCanvasFactory(factory: CanvasFactory | null): void {
  override = factory;
}

/** True when `createCanvas` can produce a canvas in this environment. */
export function canCreateCanvas(): boolean {
  return (
    override !== null ||
    (typeof document !== 'undefined' && typeof document.createElement === 'function') ||
    typeof OffscreenCanvas !== 'undefined'
  );
}

/** A new canvas of at least 1 × 1 px (sizes are rounded up). Throws when none is available. */
export function createCanvas(width: number, height: number): Canvas2D {
  const w = Math.max(1, Math.ceil(Number.isFinite(width) ? width : 1));
  const h = Math.max(1, Math.ceil(Number.isFinite(height) ? height : 1));
  if (override) return override(w, h);
  if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  throw new Error('ball.io: no canvas implementation available');
}

/** The canvas's 2D context, or null when the browser refuses one. */
export function get2d(canvas: Canvas2D, settings?: CanvasRenderingContext2DSettings): Ctx2D | null {
  try {
    return (canvas as HTMLCanvasElement).getContext('2d', settings) as Ctx2D | null;
  } catch {
    return null;
  }
}

/** Sets a canvas's pixel size, touching it only when it changes (a resize clears the bitmap). */
export function sizeCanvas(canvas: Canvas2D, width: number, height: number): void {
  const w = Math.max(1, Math.ceil(width));
  const h = Math.max(1, Math.ceil(height));
  if (canvas.width !== w) canvas.width = w;
  if (canvas.height !== h) canvas.height = h;
}

/** Pixel size of any drawable image (an HTMLImageElement reports its natural size). */
export function imageSize(img: CanvasImageSource): { w: number; h: number } {
  const anyImg = img as {
    naturalWidth?: number;
    naturalHeight?: number;
    width?: unknown;
    height?: unknown;
  };
  if (typeof anyImg.naturalWidth === 'number' && anyImg.naturalWidth > 0) {
    return { w: anyImg.naturalWidth, h: anyImg.naturalHeight ?? anyImg.naturalWidth };
  }
  const w = typeof anyImg.width === 'number' ? anyImg.width : 0;
  const h = typeof anyImg.height === 'number' ? anyImg.height : 0;
  return { w, h };
}

/**
 * Draws `draw` into a new `width × height` canvas and returns it, or null when no canvas or
 * context is available.
 */
export function paintCanvas(
  width: number,
  height: number,
  draw: (ctx: Ctx2D, w: number, h: number) => void,
): Canvas2D | null {
  let canvas: Canvas2D;
  try {
    canvas = createCanvas(width, height);
  } catch {
    return null;
  }
  const ctx = get2d(canvas);
  if (!ctx) return null;
  draw(ctx, canvas.width, canvas.height);
  return canvas;
}
